import type { Breadcrumb as SentryBreadcrumb, Primitive } from '@sentry/types';
import consts from '../../config/consts';
import Sentry from './sentry';

export enum Environment {
    Rendering = 'rendering',
    VideoDataServer = 'video data server',
}

type ContextData = Record<string, unknown>;
export type Context = {
    level?: 'error' | 'warning' | 'log';
    extra?: ContextData;
};

type User = {
    [key: string]: any;
    id?: string | number;
    email?: string;
    username?: string;
};

type Breadcrumb = SentryBreadcrumb;

export type LuminaryErrorReporterType = typeof LuminaryErrorReporter;

/**
 * A generic class for reporting errors to a backend service.
 * We should try to keep this generic and define our own types so that if we want to
 * swap or add another error reporting service, we can do so easily (without changing a lot of other code).
 *
 * Note that this class' interface should match the ErrorReporter interface in the web repo.
 */
class LuminaryErrorReporter {
    static init(environment: Environment) {
        const DEBUG = !process.env.DEBUG || process.env.DEBUG.toLowerCase() !== 'false';
        Sentry.init({
            dsn: DEBUG ? undefined : consts.SENTRY_DSN,
            environment: environment,
            release: process.env.COMMIT_SHA,
            // We collect our own traces and send them to Sentry from our OTEL collector
            // so we currently don't use sentry's tracing (and thus can't use their profiling)
        });
    }

    static setUser(user: User) {
        Sentry.setUser(user);
    }

    static capture(error: unknown, context?: Context) {
        context = context || {};
        if (!context.level) {
            context.level = 'error';
        }
        if (typeof error === 'string') {
            Sentry.captureMessage(error, context);
        } else {
            Sentry.captureException(error, context);
        }
    }

    static addBreadcrumb(breadcrumb: Breadcrumb) {
        Sentry.addBreadcrumb(breadcrumb);
    }

    static setContext(name: string, context: ContextData) {
        Sentry.setContext(name, context);
    }

    static setTag(name: string, value: Primitive) {
        Sentry.setTag(name, value);
    }
}

// If luminary is running in web, use the ErrorReporter from the window object
export const ErrorReporter = new Proxy({} as LuminaryErrorReporterType, {
    get(_target, prop) {
        if (typeof window !== 'undefined' && window.ErrorReporter) {
            return window.ErrorReporter[prop];
        } else {
            return LuminaryErrorReporter[prop];
        }
    },
});
